import { forwardRef, useEffect, useRef, useState } from "react";
import Sheet from "./AnimatedSheet";
import OverlayHeading from "./OverlayHeading";
import "./overlaySurfaces.css";
import "./responsiveOverlays.css";

let nextSheetId = 0;
const focusables = 'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex="0"], [contenteditable="true"]';

// PrimeReact 6 Sidebar owns Escape, modality and animation. Add form-dialog
// semantics, a keyboard loop and focus restoration without changing navigation sheets.
export default forwardRef(function ResponsiveSheet({ header, description, icon, icons, className = "", visible, onShow, onExited, ...props }, forwardedRef) {
	const [id] = useState(() => `akello-form-sheet-${++nextSheetId}`);
	const trigger = useRef(null);
	const open = useRef(false);
	// Capture before children mount: an auto-focused field must not replace the opener.
	if (visible && !open.current) trigger.current = document.activeElement;
	open.current = visible;
	useEffect(() => {
		if (!visible) return;
		const trap = (event) => {
			const root = document.getElementById(props.id || id);
			if (event.key !== "Tab" || !root?.contains(event.target)) return;
			const targets = Array.from(root.querySelectorAll(focusables)).filter((node) => (node.tabIndex >= 0 || node.matches('[contenteditable="true"]')) && node.getAttribute("aria-hidden") !== "true" && node.getClientRects().length);
			const first = targets[0], last = targets[targets.length - 1];
			if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
			else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
		};
		document.addEventListener("keydown", trap);
		return () => document.removeEventListener("keydown", trap);
	}, [visible, id, props.id]);
	const heading = header || icons;
	return <Sheet ref={(instance) => { if (typeof forwardedRef === "function") forwardedRef(instance); else if (forwardedRef) forwardedRef.current = instance; }}
		blockScroll ariaCloseLabel="Close panel" {...props} id={props.id || id} visible={visible}
		className={`akello-sheet akello-form-sheet ${className}`}
		icons={heading && <div id={`${id}-title`} className="akello-sheet-heading">{typeof heading === "string" ? <OverlayHeading title={heading} description={description} icon={icon} /> : heading}</div>}
		onShow={() => {
			const root = document.getElementById(props.id || id);
			root?.setAttribute("role", "dialog");
			root?.setAttribute("aria-modal", String(props.modal !== false));
			if (heading) root?.setAttribute("aria-labelledby", `${id}-title`);
			else root?.setAttribute("aria-label", props.ariaLabel || "Details panel");
			onShow?.();
		}}
		onExited={(...args) => { if (!open.current && !document.querySelector('.p-dialog, .p-sidebar-active')) trigger.current?.focus?.(); onExited?.(...args); }} />;
});
